/**
 * Modal de Selección de EPPs para Retiro
 *
 * Permite seleccionar múltiples EPPs desde una tabla con filtros.
 * Los items de los EPPs seleccionados se agrupan y deduplican automáticamente.
 *
 * Filtros: Solo por status AGD (Asignado) y pick: false
 * Columnas: Igual que la tabla de asignaciones EPP
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Button,
	Badge,
	Alert,
	Spinner,
} from 'reactstrap';
import { ODataGrid } from '../../../../../Components/OElements/ODataGrid';
import { getPersonalProtectiveEquipmentQuery, getWarehouseItem } from '../../../../../helpers/warehouse/backend_helper';
import { toast } from 'react-toastify';
import { toSmartTitleCase } from '../../../../../utils/toSmartTitleCase';

/**
 * Obtiene los códigos SAP de los warehouse items
 * @param {Array} warehouseItemIds - Array de IDs de warehouse items
 * @returns {Promise<Object>} Objeto con warehouse_item_id como key y code_sap como value
 */
const fetchWarehouseItemsSapCodes = async (warehouseItemIds) => {
	if (!warehouseItemIds || warehouseItemIds.length === 0) {
		return {};
	}

	const uniqueIds = [...new Set(warehouseItemIds.filter(Boolean))];
	const sapCodes = {};

	// Hacer peticiones en paralelo para todos los warehouse items
	const promises = uniqueIds.map(async (itemId) => {
		try {
			const response = await getWarehouseItem(itemId);
			const item = response?.data || response;
			if (item?.code_sap) {
				sapCodes[itemId] = item.code_sap;
			}
		} catch (error) {
			console.warn(`No se pudo obtener código SAP para warehouse item ${itemId}:`, error);
			// No lanzar error, simplemente no habrá código SAP para este item
		}
	});

	await Promise.all(promises);
	return sapCodes;
};

/**
 * Agrupa y deduplica items de EPPs seleccionados
 * Agrupa por name-size-unit_measurement_name (SIN persona) y suma cantidades
 * Las personas se registran en un array para usar en observaciones
 * IMPORTANTE: Usa códigos SAP obtenidos del API de warehouse items
 *
 * @param {Array} selectedEPPs - Array de EPPs seleccionados
 * @param {Object} sapCodesMap - Mapa de warehouse_item_id -> code_sap
 * @returns {Array} Items agrupados con formato para ItemsSectionTable
 */
const groupAndTransformItems = (selectedEPPs, sapCodesMap = {}) => {
	if (!Array.isArray(selectedEPPs) || selectedEPPs.length === 0) {
		return [];
	}

	const groupedItems = {};
	const ppeIdsByItem = {}; // Track which PPE IDs contribute to each item

	selectedEPPs.forEach(ppe => {
		if (!ppe?.assigned_items) return;

		// Obtener nombre de la persona del EPP
		const personName = ppe.person?.name || 'Sin asignar';

		ppe.assigned_items.forEach(item => {
			const {
				name,
				quantity,
				unit_measurement_name,
				size,
				price,
				warehouse_id,
				warehouse_code,
				warehouse_group_id,
				warehouse_group_code,
				warehouse_item_id,
				warehouse_category_name,
				color,
			} = item;

			// Obtener código SAP del mapa (obtenido del API)
			const code_sap = sapCodesMap[warehouse_item_id] || '';

			// Key SIN persona: agrupa solo por item (name-size-unit_measurement_name)
			const key = `${name}-${size || ''}-${unit_measurement_name}`;

			// Crear par código EPP + persona
			const eppCode = ppe.code || 'Sin código';
			const eppPersonPair = { eppCode, personName };

			if (!groupedItems[key]) {
				groupedItems[key] = {
					name,
					quantity: quantity || 0,
					unit_measurement_name,
					size: size || '',
					code_sap: code_sap,
					price: price || 0,
					color: color || '',
					warehouse_id,
					warehouse_code,
					warehouse_group_id,
					warehouse_group_code,
					warehouse_item_id,
					warehouse_category_name,
					eppPersonPairs: [eppPersonPair], // Array de pares {eppCode, personName}
				};
				ppeIdsByItem[key] = [ppe._id];
			} else {
				groupedItems[key].quantity += quantity || 0;
				// Agregar el par si este EPP específico no está ya
				const existingPair = groupedItems[key].eppPersonPairs.find(
					p => p.eppCode === eppCode && p.personName === personName,
				);
				if (!existingPair) {
					groupedItems[key].eppPersonPairs.push(eppPersonPair);
				}
				if (!ppeIdsByItem[key].includes(ppe._id)) {
					ppeIdsByItem[key].push(ppe._id);
				}
			}
		});
	});

	// Transform to ItemsSectionTable format
	return Object.entries(groupedItems).map(([key, item]) => {
		const totalAmount = (item.price || 0) * (item.quantity || 0);

		// Build description with size/color if present
		const descParts = [];
		if (item.size) descParts.push(`Talla: ${item.size}`);
		if (item.color) descParts.push(`Color: ${item.color}`);
		if (item.warehouse_category_name)
			descParts.push(`${item.warehouse_category_name}`);

		// Nombre del item SIN persona (solo el nombre del item)
		const itemName = item.name;

		return {
			NmbItem: itemName,
			QtyItem: item.quantity || 0,
			UnmdItem: (item.unit_measurement_name || '').toUpperCase(),
			PrcItem: item.price || 0,
			MontoItem: totalAmount,
			DscItem: descParts.join(' | '),
			VlrCodigo: item.code_sap || '',
			TpoCodigo: item.code_sap ? 'INT1' : '',
			eppPersonPairs: item.eppPersonPairs, // Array de pares {eppCode, personName}
			// Metadata crítica para el workflow de retiro
			_module_vars: {
				personal_protective_equipment_ids: ppeIdsByItem[key],
				warehouse_id: item.warehouse_id || '',
				warehouse_code: item.warehouse_code || '',
				warehouse_group_id: item.warehouse_group_id || '',
				warehouse_group_code: item.warehouse_group_code || '',
				warehouse_item_id: item.warehouse_item_id || '',
				warehouse_category_name: item.warehouse_category_name || '',
				size: item.size || '',
				color: item.color || '',
				epp_qty_max: item.quantity,
				epp_person_pairs: item.eppPersonPairs, // Pares código+persona para referencia
			},
		};
	});
};

/**
 * Genera el texto de observaciones para retiro EPP
 * Formato:
 * - Línea 1: "SOLO TRASLADO NO CONSTITUYE VENTA"
 * - Línea 2: "DETALLE: Código_EPP1 = Persona1, Código_EPP2 = Persona2, ..."
 *
 * NOTA: Usa los códigos EPP (asignaciones) NO los códigos SAP de los items
 * Cada código EPP va SIEMPRE junto a su persona específica (pares)
 *
 * @param {Array} groupedItems - Items agrupados con eppPersonPairs
 * @returns {string} Texto de observaciones
 */
const generateObservations = groupedItems => {
	if (!Array.isArray(groupedItems) || groupedItems.length === 0) {
		return '';
	}

	// Formato por pares: Código_EPP = Persona (cada EPP con su persona asignada)
	// Usamos eppPersonPairs que contiene {eppCode, personName}
	const allPairs = [];
	groupedItems.forEach(item => {
		if (item.eppPersonPairs && item.eppPersonPairs.length > 0) {
			item.eppPersonPairs.forEach(pair => {
				allPairs.push(`${pair.eppCode} = ${pair.personName}`);
			});
		}
	});

	if (allPairs.length === 0) {
		return 'SOLO TRASLADO NO CONSTITUYE VENTA.';
	}

	return `SOLO TRASLADO NO CONSTITUYE VENTA. DETALLE: ${allPairs.join(', ')}`;
};

export const RetiroEPPSelectorModal = ({ isOpen, onClose, onConfirm }) => {
	const [rowSelection, setRowSelection] = useState({});
	const [eppData, setEppData] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isConfirming, setIsConfirming] = useState(false);
	const [error, setError] = useState(null);

	// Load EPP data when modal opens
	useEffect(() => {
		if (isOpen) {
			loadEPPData();
		}
	}, [isOpen]);

	// Reset selection when modal closes
	useEffect(() => {
		if (!isOpen) {
			setRowSelection({});
		}
	}, [isOpen]);

	/**
	 * Carga los EPPs disponibles para retiro
	 * Solo filtra por status AGD (Asignado) y pick: false
	 */
	const loadEPPData = async () => {
		setIsLoading(true);
		setError(null);

		try {
			const response = await getPersonalProtectiveEquipmentQuery({
				filters: {
					pick: { $eq: false },
					personal_protective_equipment_status: {
						code: { $eq: 'AGD' },
					},
				},
				populate: [
					'warehouse',
					'establishment',
					'person',
					'personal_protective_equipment_status',
					'personal_protective_equipment_reason',
				],
				pagination: {
					page: 1,
					pageSize: 500,
				},
				sort: ['code:desc'],
			});

			setEppData(response?.data || []);
		} catch (err) {
			console.error('Error cargando EPPs:', err);
			setError('Error al cargar los EPPs disponibles');
			toast.error('Error al cargar los EPPs');
		} finally {
			setIsLoading(false);
		}
	};

	// Column definitions - Igual que la tabla de asignaciones EPP
	const columns = useMemo(
		() => [
			{
				header: 'Código',
				accessorKey: 'code',
				size: 100,
				dataType: 'string',
				defaultSort: 'desc',
			},
			{
				header: 'Estado',
				accessorKey: 'personal_protective_equipment_status.name',
				size: 100,
				dataType: 'multi-select',
				accessorFn: row => row.personal_protective_equipment_status?.name,
				Cell: ({ row }) => {
					const status = row.original.personal_protective_equipment_status;
					if (!status) return '-';
					return (
						<span
							className='badge'
							style={{
								backgroundColor: status.bgcolor || '#0ab39c',
								color: status.txcolor || '#fff',
							}}
						>
							{status.name}
						</span>
					);
				},
			},
			{
				header: 'Fecha',
				accessorKey: 'assignment_date',
				size: 100,
				dataType: 'date',
				Cell: ({ row }) => {
					const date = row.original.assignment_date;
					if (!date) return '-';
					return new Date(date).toLocaleDateString('es-CL', {
						year: 'numeric',
						month: '2-digit',
						day: '2-digit',
					});
				},
			},
			{
				header: 'Origen',
				accessorKey: 'warehouse.name',
				size: 150,
				dataType: 'multi-select',
				accessorFn: row => row.warehouse?.name,
				Cell: ({ row }) => {
					return toSmartTitleCase(row.original.warehouse?.name) || '-';
				},
			},
			{
				header: 'Destino',
				accessorKey: 'establishment.name',
				size: 150,
				dataType: 'multi-select',
				accessorFn: row => row.establishment?.name,
				Cell: ({ row }) => {
					return row.original.establishment?.name || '-';
				},
			},
			{
				header: 'Persona',
				accessorKey: 'person.name',
				size: 150,
				dataType: 'multi-select',
				accessorFn: row => row.person?.name,
			},
			{
				header: 'Motivo',
				accessorKey: 'personal_protective_equipment_reason.name',
				size: 120,
				dataType: 'multi-select',
				accessorFn: row => row.personal_protective_equipment_reason?.name,
				Cell: ({ row }) => {
					const reason = row.original.personal_protective_equipment_reason;
					if (!reason) return '-';
					return (
						<span
							className='badge'
							style={{
								backgroundColor: reason.bgcolor || '#299cdb',
								color: reason.txcolor || '#fff',
							}}
						>
							{reason.name}
						</span>
					);
				},
			},
		],
		[]
	);

	// Get selected EPPs based on rowSelection
	const selectedEPPs = useMemo(() => {
		const selectedIds = Object.keys(rowSelection).filter(
			id => rowSelection[id]
		);
		return eppData.filter(ppe => selectedIds.includes(ppe._id));
	}, [rowSelection, eppData]);

	// Preview of grouped items
	const groupedItemsPreview = useMemo(() => {
		return groupAndTransformItems(selectedEPPs);
	}, [selectedEPPs]);

	// Handle confirm button
	const handleConfirm = async () => {
		setIsConfirming(true);
		
		try {
			// 1. Extraer todos los warehouse_item_id únicos de los EPPs seleccionados
			const warehouseItemIds = [];
			selectedEPPs.forEach(ppe => {
				if (ppe?.assigned_items) {
					ppe.assigned_items.forEach(item => {
						if (item.warehouse_item_id) {
							warehouseItemIds.push(item.warehouse_item_id);
						}
					});
				}
			});

			// 2. Obtener códigos SAP del API
			const sapCodesMap = await fetchWarehouseItemsSapCodes(warehouseItemIds);

			// 3. Transformar items con los códigos SAP obtenidos
			const transformedItems = groupAndTransformItems(selectedEPPs, sapCodesMap);
			const observations = generateObservations(transformedItems);

			onConfirm(transformedItems, selectedEPPs, observations);
			onClose();
		} catch (error) {
			console.error('[RetiroEPPSelectorModal] Error al confirmar:', error);
			toast.error('Error al procesar los items seleccionados');
		} finally {
			setIsConfirming(false);
		}
	};

	return (
		<Modal isOpen={isOpen} toggle={onClose} size='xl' centered>
			<ModalHeader toggle={onClose}>
				<i className='ri-checkbox-multiple-line me-2'></i>
				Seleccionar EPPs para Retiro
			</ModalHeader>
			<ModalBody>
				{isLoading ? (
					<div className='text-center py-5'>
						<Spinner color='primary' />
						<p className='mt-2 text-muted'>
							Cargando EPPs disponibles...
						</p>
					</div>
				) : error ? (
					<Alert color='danger'>
						<i className='ri-error-warning-line me-2'></i>
						{error}
						<Button
							color='link'
							size='sm'
							onClick={loadEPPData}
							className='ms-2'
						>
							Reintentar
						</Button>
					</Alert>
				) : eppData.length === 0 ? (
					<Alert color='info'>
						<i className='ri-information-line me-2'></i>
						No hay EPPs disponibles para retiro.
					</Alert>
				) : (
					<>
						<Alert color='info' className='py-2 mb-3'>
							<i className='ri-information-line me-2'></i>
							Seleccione los EPPs asignados que serán retirados. Los
							items se agruparán automáticamente.
						</Alert>

						<ODataGrid
							data={eppData}
							columns={columns}
							remoteOperations={false}
							enableRowSelection={() => true}
							selectAllMode='all'
							getRowIdTable={row => row._id}
							selection={{
								rowSelection,
								setRowSelection,
							}}
							mantineTableContainerProps={{
								style: { maxHeight: '350px' },
							}}
							defaultPageSize={20}
							nameFile='epps_para_retiro'
						/>

						{/* Preview of grouped items */}
						{selectedEPPs.length > 0 && (
							<div className='mt-3 p-3 bg-light rounded'>
								<h6 className='mb-2'>
									<i className='ri-list-check-2 me-2'></i>
									Vista previa de items agrupados:
								</h6>
								<div
									style={{
										maxHeight: '150px',
										overflowY: 'auto',
									}}
								>
									{groupedItemsPreview.map((item, idx) => (
										<div
											key={idx}
											className='py-1 border-bottom'
										>
											<div className='d-flex justify-content-between align-items-center'>
												<span>
													<strong>{item.NmbItem}</strong>
													{item.DscItem && (
														<small className='text-muted ms-2'>
															({item.DscItem})
														</small>
													)}
												</span>
												<Badge color='primary'>
													{item.QtyItem} {item.UnmdItem}
												</Badge>
											</div>
											{item.eppPersonPairs && item.eppPersonPairs.length > 0 && (
												<small className='text-muted d-block'>
													{item.eppPersonPairs.map((pair, pidx) => (
														<span key={pidx} className='me-2'>
															<i className='ri-user-line me-1'></i>
															{pair.eppCode} = {pair.personName}
															{pidx < item.eppPersonPairs.length - 1 ? ', ' : ''}
														</span>
													))}
												</small>
											)}
										</div>
									))}
									</div>
								</div>
							)}
					</>
				)}
			</ModalBody>
			<ModalFooter>
				<div className='d-flex justify-content-between w-100 align-items-center'>
					<div>
						{selectedEPPs.length > 0 && (
							<>
								<Badge color='success' className='me-2'>
									{selectedEPPs.length} EPP(s) seleccionados
								</Badge>
								<Badge color='info'>
									{groupedItemsPreview.length} item(s) agrupados
								</Badge>
							</>
						)}
					</div>
					<div className='d-flex gap-2'>
						<Button color='secondary' onClick={onClose} disabled={isConfirming}>
							<i className='ri-close-line me-1'></i>
							Cancelar
						</Button>
						<Button
							color='primary'
							onClick={handleConfirm}
							disabled={selectedEPPs.length === 0 || isConfirming}
						>
							{isConfirming ? (
								<>
									<Spinner size='sm' className='me-1' />
									Obteniendo códigos SAP...
								</>
							) : (
								<>
									<i className='ri-check-line me-1'></i>
									Confirmar Selección
								</>
							)}
						</Button>
					</div>
				</div>
			</ModalFooter>
		</Modal>
	);
};

export default RetiroEPPSelectorModal;
